import { Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { BackIcon } from './Icons';
import PATH from "@config/PATH";

/**
 * Header dùng chung: nút back + tiêu đề + icon phụ (tuỳ chọn) bên phải.
 *
 * `trailIcon` nhận một React node (thường là component trong
 * `@shared_components/Icons`), ví dụ: `trailIcon={<SettingsIcon />}`.
 *
 * `className` ghi đè lớp ngoài cùng (mặc định chỉ là padding) — dùng cho các
 * trang dựng trên canvas 880px cố định cần padding/vị trí riêng theo design.
 */
export default function Headers({
  title = "",
  titleColor = "#713B12",
  onBack = null,
  onTrailIconClick,
  trailIcon = null,
  className = "px-2 sm:px-5",
  children = null,
  iconSizeCustom,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBack = () => (onBack ? onBack() : navigate(PATH.HOME, { replace: true }));

  // Nút back/trail giữ tỷ lệ 96/96 và co giãn liên tục theo bề ngang header
  // (design: icon 96px trên header 684px = 14.04cqw) thay vì nhảy bậc theo
  // breakpoint. Kích thước dùng chung cho cả ô trống bên phải để tiêu đề luôn
  // nằm chính giữa.
  const iconSize = "size-[clamp(40px,14cqw,76px)] aspect-square shrink-0";
  return (
    <div className={`@container flex items-center justify-between ${className} z-2`}>
      <button
        type="button"
        onClick={handleBack}
        aria-label={t("common.back")}
        className={`btn-click-active grid cursor-pointer place-items-center outline-none ${iconSize} ${iconSizeCustom}`}
      >
        <BackIcon className="size-full object-contain" />
      </button>

      <Text
        as="p"
        style={{ color: titleColor }}
        className="w-full text-center text-[clamp(20px,7cqw,48px)] font-bold"
      >
        {t(title)}
      </Text>

      {trailIcon ? (
        <button
          type="button"
          onClick={onTrailIconClick}
          className={`btn-click-active grid cursor-pointer place-items-center ${iconSize}`}
        >
          {trailIcon}
        </button>
      ) : (
        /* Ô trống cân bằng với nút back để tiêu đề nằm chính giữa */
        children ? children : <div className={`${iconSize} ${iconSizeCustom}`} aria-hidden />
      )}
    </div>
  );
}
